import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, Music, Camera, Disc3, CreditCard } from "lucide-react";
import { DjRoomPerks } from "@/components/DjRoomPerks";
import { AfterpayToday } from "@/components/booking/PayLaterCalculator";
import { FilmedVideo } from "@/components/home/FilmedSetsSection";

const packages = [
  {
    title: "DMRG Studio Experience Package",
    price: "$400",
    features: ["2 Hours Recording Time", "One Mix", "One Master"],
    popular: true
  },
  {
    title: "Dream Maker Sessions Popular Package (DJ Room)",
    price: "$60",
    features: ["2 Hours Room Hire", "Free Water and Soft Drink", "Fast WiFi", "Sennheiser HD 25 Headphones Included"],
    popular: false
  },
  {
    title: "Dream Maker Sessions + Filmed (Film Your Set / Content Session)",
    price: "$125",
    priceNote: "Flat rate · 2 hours",
    features: ["2 Hours DJ Room Hire", "Set Filmed on Nikon Z5 + GoPro", "Video Filmed for Socials", "High-Quality Audio Recording of Your Full Set", "Raw Clips Delivered to You", "Free Water and Soft Drink", "Sennheiser HD 25 Headphones Included"],
    popular: false,
    isNew: true
  },
  {
    title: "Photography (Press Shots)",
    price: "$299.99",
    features: ["30+ Edited Photos", "Professional Lighting", "Various Backdrops", "For Artists, Creatives & Business"],
    popular: false
  },
  {
    title: "Ableton / FL Studio Production Course (Lister)",
    price: "$1,250",
    features: ["4 Weeks Program", "8 Hours Total (2 hrs/week)", "1-on-1 With Lister", "All Levels Welcome"],
    popular: false
  },
  {
    title: "Ableton Live Production Course (Alex M · 8 Weeks)",
    price: "$2,499",
    features: ["8 Weeks Program", "16 Hours Total (2 hrs/week)", "1-on-1 With Alex M (Hardstyle/Rawstyle)", "Sample Packs, Templates, Presets", "1 Month FREE Harder Class"],
    popular: false
  },
  {
    title: "Ultimate DMRG Experience",
    price: "$1000",
    features: ["2 Hours Studio Time", "One Mix", "One Master", "Artist Press Shots", "Behind The Scenes Footage / Vlog", "Artwork for Release"],
    popular: false
  }
];

const pricingCategories = [
  {
    title: "Recording Services",
    icon: Music,
    items: [
      { name: "Recording (With Engineer)", price: "$100", unit: "/hour", description: "Professional recording with our experienced engineer" },
      { name: "Recording (Dry Hire)", price: "$90", unit: "/hour", description: "Studio access without engineer" },
      { name: "Rehearsal – Half Day", price: "$150", unit: "4 hours", description: "Perfect for band rehearsals" },
      { name: "Rehearsal – Full Day", price: "$300", unit: "8 hours", description: "Extended rehearsal sessions" },
    ]
  },
  {
    title: "Mixing & Mastering",
    icon: Clock,
    items: [
      { name: "Mixing (1 Song)", price: "$400", unit: "/song", description: "Full mix for a single track" },
      { name: "Mixing (2-3 Songs)", price: "$375", unit: "/song", description: "Discounted rate for EP projects" },
      { name: "Mixing (4-7 Songs)", price: "$350", unit: "/song", description: "Album project rate" },
      { name: "Mixing (8+ Songs)", price: "$325", unit: "/song", description: "Best value for full albums" },
      { name: "Mastering", price: "$100", unit: "/song", description: "Professional mastering" },
    ]
  },
  {
    title: "Production & Rooms",
    icon: Disc3,
    items: [
      { name: "DJ Room Hire (Dream Makers Sessions)", price: "$30", unit: "/hour", description: "Pioneer CDJ-3000s + DJM-A9. Free water & soft drinks, fast WiFi and Sennheiser HD 25 headphones included. ID required. Late-night (12AM–4AM): $100/hr." },
      { name: "Dream Maker Sessions + Filmed", price: "$125", unit: "flat · 2 hours", description: "Film your set: shot on Nikon Z5 + GoPro, full set audio recorded, raw clips delivered. ID required." },
      { name: "DMRG Afterdark Session", price: "$100", unit: "/hour · 12AM–4AM", description: "Full studio shutdown after dark. Minimum 2 hours ($200), up to 4 hours 12 AM–4 AM. ID required." },
      { name: "Production Suite Hire", price: "$40", unit: "/hour", description: "Private production room" },
    ]
  },
  {
    title: "Photography",
    icon: Camera,
    items: [
      { name: "Photo Press Shots", price: "$299.99", unit: "30 photos", description: "Professional artist photography with 30+ edited photos" },
      { name: "Photography (Dry Hire)", price: "$80", unit: "/hour", description: "Studio space only" },
    ]
  }
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export default function Pricing() {
  return (
    <div className="pt-20" data-testid="pricing-page">
      {/* Hero */}
      <section className="py-24 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Pricing</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Transparent Pricing
            </h1>
            <p className="text-white/60 text-lg">
              Quality studio services at competitive rates. No hidden fees, no surprises.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Afterpay & Zip Pay Banner */}
      <section className="py-8 bg-gradient-to-r from-dream-violet/20 via-purple-500/20 to-pink-500/20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-center md:text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <CreditCard className="w-7 h-7 text-dream-violet" />
              </div>
              <div>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-white">
                  Afterpay & Zip Pay Available
                </h3>
                <p className="text-white/70 text-sm md:text-base">
                  Simply pay in 4 interest-free instalments
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-white">4</span>
                <span className="text-white/50 text-xs">Payments</span>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-green-400">0%</span>
                <span className="text-white/50 text-xs">Interest</span>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-white">Easy</span>
                <span className="text-white/50 text-xs">Checkout</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Popular Packages Section */}
      <section className="py-24 bg-dream-bg" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Value Deals</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Popular Packages
            </h2>
            <p className="text-white/60 max-w-xl mx-auto">
              Get more value with our curated packages designed for artists at every stage.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className={`relative glow-card p-8 rounded-2xl ${pkg.popular ? 'border-dream-violet' : ''}`}
                data-testid={`package-${index}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-dream-violet text-white text-xs font-bold px-4 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                {pkg.isNew && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 bg-cyan-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                    NEW · CONTENT SESSION
                  </div>
                )}
                {pkg.isNew && (
                  <div className="-mx-8 -mt-8 mb-6 h-56 overflow-hidden rounded-t-2xl" data-testid="pricing-filmed-video">
                    <FilmedVideo testId="pricing-filmed-video-el" />
                  </div>
                )}
                <h3 className="font-heading text-xl font-bold text-white mb-2 mt-2">{pkg.title}</h3>
                <div className="mb-6">
                  <span className="font-mono text-4xl font-bold text-dream-violet">{pkg.price}</span>
                  {pkg.priceNote && <span className="block text-white/50 text-sm mt-1">{pkg.priceNote}</span>}
                  <AfterpayToday price={pkg.price} className="mt-1" testId={`pricing-package-afterpay-${index}`} />
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-white/80 text-sm">
                      <div className="w-2 h-2 bg-dream-violet rounded-full flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/booking">
                  <Button className={`w-full rounded-full ${pkg.popular ? 'bg-dream-violet text-white hover:bg-dream-violet-glow' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                    Book Package
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-8 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6">
          <DjRoomPerks testId="pricing-dj-room-perks" />
        </div>
      </section>

      {/* Pricing Tables */}
      <section className="py-16 bg-dream-surface">
        <div className="max-w-7xl mx-auto px-6 space-y-16">
          {pricingCategories.map((category, catIndex) => (
            <motion.div
              key={category.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: catIndex * 0.1 }}
              data-testid={`pricing-category-${catIndex}`}
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-dream-violet/10 flex items-center justify-center">
                  <category.icon className="w-6 h-6 text-dream-violet" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
                  {category.title}
                </h2>
              </div>

              <div className="grid gap-4">
                {category.items.map((item, itemIndex) => (
                  <motion.div
                    key={item.name}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    transition={{ delay: itemIndex * 0.05 }}
                    className="glow-card p-6 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    data-testid={`pricing-item-${catIndex}-${itemIndex}`}
                  >
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-white text-lg mb-1">{item.name}</h3>
                      <p className="text-white/50 text-sm">{item.description}</p>
                    </div>
                    <div className="flex items-baseline gap-1 sm:text-right">
                      <span className="font-mono text-3xl font-bold text-dream-violet">{item.price}</span>
                      <span className="text-white/50 text-sm">{item.unit}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Notes Section */}
      <section className="py-16 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="glass rounded-2xl p-8 md:p-12"
          >
            <h3 className="font-heading text-2xl font-bold text-white mb-6">Important Information</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-white mb-3">Studio Hours</h4>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li><span className="text-white">Recording Studio:</span> Mon-Thu 5PM-12AM, Fri-Sun 9AM-2AM</li>
                  <li><span className="text-white">Production Room:</span> Same as Recording Studio</li>
                  <li><span className="text-white">DJ Room:</span> 12PM–4AM, 7 days a week</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-3">Booking Requirements</h4>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li>All prices are in AUD and include GST</li>
                  <li>DJ Room bookings require valid photo ID upload</li>
                  <li>Payment required at time of booking</li>
                  <li>Cancellation policy: 24 hours notice required</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-dream-surface">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Book Your Session?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-8">
              Choose your service, pick your time slots, and secure your session online.
            </p>
            <Link to="/booking">
              <Button 
                data-testid="pricing-book-btn"
                className="bg-white text-black hover:bg-dream-violet hover:text-white rounded-full px-10 py-6 text-lg font-bold transition-all duration-300"
              >
                Book Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
